import { useCallback, useEffect, useRef, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { type ReadAloudStatus } from "../../../components/chatNewInterface/interfaceComponents/messageBubble/AITutorMessageBubble";
import type { ReadAloudControl } from "../../../components/chatNewInterface/interfaceComponents/messageBubble/ReadAloudPlaybackControl";
import type { MessageItem } from "../../companion/companionMessage.types";
import { useLearningMessageTts } from "../../../hooks/useLearningMessageTts";
import { stripMarkdownForTts } from "../../../utils/stripMarkdownForTts";
import { type PlaybackSpeed } from "../../../utils/toolPreferenceStorage";

export interface MessageReadAloudTtsDeps {
  resetPlayback: () => void | Promise<void>;
  suspendPlayback: () => void;
  resumePlayback: () => void;
  getTtsToken: () => Promise<string | null>;
  onAutoStreamReadAloudStop?: (messageId: string) => void;
}

const READ_ALOUD_TICK_MS = 1000;
const READ_ALOUD_WORDS_PER_MINUTE = 150;
const READ_ALOUD_MIN_SECONDS = 3;
const STREAM_AUTO_END_BUFFER_SECONDS = 12;

interface ReadAloudState {
  status: ReadAloudStatus;
  isActive: boolean;
  hasPlayed: boolean;
  elapsedSeconds: number;
  totalSeconds: number;
  isStreamAuto?: boolean;
}

const createDefaultReadAloudState = (): ReadAloudState => ({
  status: "paused",
  isActive: false,
  hasPlayed: false,
  elapsedSeconds: 0,
  totalSeconds: 0,
  isStreamAuto: false,
});

const estimateReadAloudDurationSeconds = (content: string) => {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  const estimatedSeconds = Math.ceil(words / (READ_ALOUD_WORDS_PER_MINUTE / 60));
  return Math.max(READ_ALOUD_MIN_SECONDS, estimatedSeconds || READ_ALOUD_MIN_SECONDS);
};

export interface UseAITutorMessagesReadAloudParams {
  resourceId: string;
  messages: MessageItem[];
  isStreaming: boolean;
  currentStreamingMessageId: string | null | undefined;
  messageReadAloudTts: MessageReadAloudTtsDeps;
  isAutoStreamReadAloudEnabled: boolean;
  autoStreamPlaybackStartedEvent?: { messageId: string; nonce: number } | null;
  autoStreamPlaybackEndedEvent: { messageId: string; nonce: number } | null;
  readAloudMessageIdRemapEvent?: { fromMessageId: string; toMessageId: string; nonce: number } | null;
  playbackSpeed: PlaybackSpeed;
  isScreenFocused: boolean;
  onUserFocusCleanup: () => void;
}

export interface StreamReadAloudMeters {
  streamAudioLevel: number;
  streamAudioLevels: number[];
}

export function useAITutorMessagesReadAloud({
  resourceId,
  messages,
  isStreaming,
  currentStreamingMessageId,
  messageReadAloudTts,
  isAutoStreamReadAloudEnabled,
  autoStreamPlaybackStartedEvent = null,
  autoStreamPlaybackEndedEvent,
  readAloudMessageIdRemapEvent = null,
  playbackSpeed,
  isScreenFocused,
  onUserFocusCleanup,
}: UseAITutorMessagesReadAloudParams) {
  const [readAloudByMessageId, setReadAloudByMessageId] = useState<Record<string, ReadAloudState>>({});
  const readAloudByMessageIdRef = useRef<Record<string, ReadAloudState>>({});
  const readAloudLoadingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const readAloudPlaybackIntervalsRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const messageReadAloudTtsRef = useRef(messageReadAloudTts);
  messageReadAloudTtsRef.current = messageReadAloudTts;
  const playbackSpeedRef = useRef(playbackSpeed);
  playbackSpeedRef.current = playbackSpeed;
  const googlePlaybackMessageIdRef = useRef<string | null>(null);
  const startReadAloudPlaybackIntervalRef = useRef<(messageId: string) => void>(() => {});
  const lastStreamingMessageIdRef = useRef<string | null>(null);
  const onUserFocusCleanupRef = useRef(onUserFocusCleanup);
  onUserFocusCleanupRef.current = onUserFocusCleanup;
  const getTtsTokenWrapper = useCallback(async () => {
    return await messageReadAloudTtsRef.current.getTtsToken();
  }, []);

  const {
    startSession: startMessageTtsSession,
    stopSession: stopMessageTtsSession,
    pauseSession: pauseMessageTtsSession,
    resumeSession: resumeMessageTtsSession,
    setHandlers: setMessageTtsHandlers,
  } = useLearningMessageTts({
    getToken: getTtsTokenWrapper,
    playbackRate: playbackSpeed,
  });

  const clearReadAloudLoadingTimer = useCallback((messageId: string) => {
    const timeoutId = readAloudLoadingTimeoutsRef.current[messageId];
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      delete readAloudLoadingTimeoutsRef.current[messageId];
    }
  }, []);

  const clearReadAloudPlaybackInterval = useCallback((messageId: string) => {
    const intervalId = readAloudPlaybackIntervalsRef.current[messageId];
    if (intervalId != null) {
      clearInterval(intervalId);
      delete readAloudPlaybackIntervalsRef.current[messageId];
    }
  }, []);

  const clearReadAloudTimersForMessage = useCallback(
    (messageId: string) => {
      clearReadAloudLoadingTimer(messageId);
      clearReadAloudPlaybackInterval(messageId);
    },
    [clearReadAloudLoadingTimer, clearReadAloudPlaybackInterval]
  );

  const clearAllReadAloudTimers = useCallback(() => {
    Object.keys(readAloudLoadingTimeoutsRef.current).forEach((id) => {
      clearTimeout(readAloudLoadingTimeoutsRef.current[id]);
      delete readAloudLoadingTimeoutsRef.current[id];
    });
    Object.keys(readAloudPlaybackIntervalsRef.current).forEach((id) => {
      clearInterval(readAloudPlaybackIntervalsRef.current[id]);
      delete readAloudPlaybackIntervalsRef.current[id];
    });
  }, []);

  useEffect(() => {
    readAloudByMessageIdRef.current = readAloudByMessageId;
  }, [readAloudByMessageId]);

  useEffect(() => {
    const startedMessageId = autoStreamPlaybackStartedEvent?.messageId;
    if (!startedMessageId || !isAutoStreamReadAloudEnabled) {
      return;
    }
    const content = messages.find((m) => m.id === startedMessageId)?.content ?? "";
    const existing = readAloudByMessageIdRef.current[startedMessageId] ?? createDefaultReadAloudState();
    if (!existing.isActive || existing.status !== "playing") {
      setReadAloudByMessageId((prev) => ({
        ...prev,
        [startedMessageId]: {
          ...(prev[startedMessageId] ?? createDefaultReadAloudState()),
          isActive: true,
          status: "playing",
          hasPlayed: true,
          isStreamAuto: true,
          totalSeconds: Math.max(prev[startedMessageId]?.totalSeconds ?? 0, estimateReadAloudDurationSeconds(content)),
        },
      }));
    }
    if (!readAloudPlaybackIntervalsRef.current[startedMessageId]) {
      const t = setTimeout(() => {
        startReadAloudPlaybackIntervalRef.current(startedMessageId);
      }, 0);
      return () => {
        clearTimeout(t);
      };
    }
    return;
  }, [autoStreamPlaybackStartedEvent, isAutoStreamReadAloudEnabled, messages]);

  useEffect(() => {
    if (!isAutoStreamReadAloudEnabled || !isStreaming || currentStreamingMessageId == null) {
      return;
    }
    const msgId = currentStreamingMessageId;
    const content = messages.find((m) => m.id === msgId)?.content ?? "";
    const existing = readAloudByMessageIdRef.current[msgId] ?? createDefaultReadAloudState();
    if (!existing.isActive || existing.status !== "playing") {
      setReadAloudByMessageId((prev) => ({
        ...prev,
        [msgId]: {
          ...(prev[msgId] ?? createDefaultReadAloudState()),
          isActive: true,
          status: "playing",
          hasPlayed: true,
          isStreamAuto: true,
          totalSeconds: Math.max(prev[msgId]?.totalSeconds ?? 0, estimateReadAloudDurationSeconds(content)),
        },
      }));
    }
    if (!readAloudPlaybackIntervalsRef.current[msgId]) {
      const t = setTimeout(() => {
        startReadAloudPlaybackIntervalRef.current(msgId);
      }, 0);
      return () => {
        clearTimeout(t);
      };
    }
    return;
  }, [currentStreamingMessageId, isAutoStreamReadAloudEnabled, isStreaming, messages]);

  useEffect(() => {
    if (isStreaming && currentStreamingMessageId != null) {
      lastStreamingMessageIdRef.current = currentStreamingMessageId;
      return;
    }
    if (isStreaming) {
      return;
    }
    const endedId = lastStreamingMessageIdRef.current;
    if (endedId == null) {
      return;
    }
    lastStreamingMessageIdRef.current = null;
    if (googlePlaybackMessageIdRef.current === endedId) {
      return;
    }
    if (isAutoStreamReadAloudEnabled) {
      const content = messages.find((m) => m.id === endedId)?.content ?? "";
      const existing = readAloudByMessageIdRef.current[endedId] ?? createDefaultReadAloudState();
      const estimatedFullSeconds = estimateReadAloudDurationSeconds(content);
      const adjustedTotalSeconds = Math.max(
        existing.totalSeconds,
        estimatedFullSeconds,
        existing.elapsedSeconds + STREAM_AUTO_END_BUFFER_SECONDS
      );
      setReadAloudByMessageId((prev) => ({
        ...prev,
        [endedId]: {
          ...existing,
          isActive: true,
          status: "playing",
          hasPlayed: true,
          isStreamAuto: true,
          totalSeconds: adjustedTotalSeconds,
        },
      }));
      if (!readAloudPlaybackIntervalsRef.current[endedId]) {
        const t = setTimeout(() => {
          startReadAloudPlaybackIntervalRef.current(endedId);
        }, 0);
        return () => {
          clearTimeout(t);
        };
      }
      return;
    }
    const endedState = readAloudByMessageIdRef.current[endedId];
    if (!endedState?.isActive || endedState.status !== "playing") {
      return;
    }
    clearReadAloudPlaybackInterval(endedId);
    setReadAloudByMessageId((prev) => ({
      ...prev,
      [endedId]: {
        ...(prev[endedId] ?? createDefaultReadAloudState()),
        isActive: false,
        status: "paused",
        elapsedSeconds: 0,
        hasPlayed: prev[endedId]?.hasPlayed ?? true,
      },
    }));
  }, [clearReadAloudPlaybackInterval, currentStreamingMessageId, isAutoStreamReadAloudEnabled, isStreaming, messages]);

  useEffect(() => {
    const endedMessageId = autoStreamPlaybackEndedEvent?.messageId;
    if (!endedMessageId) {
      return;
    }
    const currentState = readAloudByMessageIdRef.current[endedMessageId];
    if (!currentState?.isStreamAuto) {
      return;
    }
    clearReadAloudPlaybackInterval(endedMessageId);
    setReadAloudByMessageId((prev) => {
      const st = prev[endedMessageId] ?? createDefaultReadAloudState();
      return {
        ...prev,
        [endedMessageId]: {
          ...st,
          status: "paused",
          isActive: false,
          hasPlayed: true,
          elapsedSeconds: Math.min(st.elapsedSeconds, st.totalSeconds),
          isStreamAuto: false,
        },
      };
    });
  }, [autoStreamPlaybackEndedEvent, clearReadAloudPlaybackInterval]);

  useEffect(() => {
    const fromMessageId = readAloudMessageIdRemapEvent?.fromMessageId;
    const toMessageId = readAloudMessageIdRemapEvent?.toMessageId;
    if (!fromMessageId || !toMessageId || fromMessageId === toMessageId) {
      return;
    }

    setReadAloudByMessageId((prev) => {
      const fromState = prev[fromMessageId];
      if (!fromState) {
        return prev;
      }
      const toState = prev[toMessageId];
      const mergedToState = toState
        ? {
            ...fromState,
            ...toState,
            totalSeconds: Math.max(fromState.totalSeconds, toState.totalSeconds),
            elapsedSeconds: Math.max(fromState.elapsedSeconds, toState.elapsedSeconds),
          }
        : fromState;
      const next = { ...prev, [toMessageId]: mergedToState };
      delete next[fromMessageId];
      return next;
    });

    const loadingTimer = readAloudLoadingTimeoutsRef.current[fromMessageId];
    if (loadingTimer != null) {
      delete readAloudLoadingTimeoutsRef.current[fromMessageId];
      readAloudLoadingTimeoutsRef.current[toMessageId] = loadingTimer;
    }
    const playbackInterval = readAloudPlaybackIntervalsRef.current[fromMessageId];
    if (playbackInterval != null) {
      delete readAloudPlaybackIntervalsRef.current[fromMessageId];
      readAloudPlaybackIntervalsRef.current[toMessageId] = playbackInterval;
    }

    if (googlePlaybackMessageIdRef.current === fromMessageId) {
      googlePlaybackMessageIdRef.current = toMessageId;
    }
    if (lastStreamingMessageIdRef.current === fromMessageId) {
      lastStreamingMessageIdRef.current = toMessageId;
    }
  }, [readAloudMessageIdRemapEvent]);

  useEffect(() => {
    const assistantMessageIds = new Set(
      messages.filter((message) => message.sender === "ai").map((message) => message.id)
    );

    setReadAloudByMessageId((prev) => {
      const next: Record<string, ReadAloudState> = {};
      assistantMessageIds.forEach((messageId) => {
        next[messageId] = prev[messageId] ?? createDefaultReadAloudState();
      });
      return next;
    });

    Object.keys(readAloudLoadingTimeoutsRef.current).forEach((messageId) => {
      if (!assistantMessageIds.has(messageId)) {
        clearReadAloudLoadingTimer(messageId);
      }
    });
    Object.keys(readAloudPlaybackIntervalsRef.current).forEach((messageId) => {
      if (!assistantMessageIds.has(messageId)) {
        clearReadAloudPlaybackInterval(messageId);
      }
    });
  }, [clearReadAloudLoadingTimer, clearReadAloudPlaybackInterval, messages]);

  useEffect(() => {
    return () => {
      clearAllReadAloudTimers();
      stopMessageTtsSession();
    };
  }, [clearAllReadAloudTimers, stopMessageTtsSession]);

  const stopReadAloudPlaybackEngines = useCallback(() => {
    stopMessageTtsSession();
    void messageReadAloudTtsRef.current.resetPlayback();
    googlePlaybackMessageIdRef.current = null;
    clearAllReadAloudTimers();
  }, [clearAllReadAloudTimers, stopMessageTtsSession]);

  const resetReadAloudUiAfterRouteBlur = useCallback(() => {
    setReadAloudByMessageId((prev) => {
      const next = { ...prev };
      for (const id of Object.keys(next)) {
        const st = next[id];
        if (!st) {
          continue;
        }
        if (st.isActive || st.status === "loading") {
          next[id] = {
            ...st,
            status: "paused",
            isActive: false,
            elapsedSeconds: st.status === "loading" ? 0 : st.elapsedSeconds,
          };
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (isScreenFocused) {
      return;
    }
    stopReadAloudPlaybackEngines();
    resetReadAloudUiAfterRouteBlur();
  }, [isScreenFocused, resetReadAloudUiAfterRouteBlur, stopReadAloudPlaybackEngines]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handlePageLeave = () => {
      stopReadAloudPlaybackEngines();
    };
    window.addEventListener("pagehide", handlePageLeave);
    window.addEventListener("beforeunload", handlePageLeave);
    return () => {
      window.removeEventListener("pagehide", handlePageLeave);
      window.removeEventListener("beforeunload", handlePageLeave);
    };
  }, [stopReadAloudPlaybackEngines]);

  useEffect(() => {
    stopReadAloudPlaybackEngines();
    resetReadAloudUiAfterRouteBlur();
  }, [resourceId, resetReadAloudUiAfterRouteBlur, stopReadAloudPlaybackEngines]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        onUserFocusCleanupRef.current();
        stopReadAloudPlaybackEngines();
        resetReadAloudUiAfterRouteBlur();
      };
    }, [resetReadAloudUiAfterRouteBlur, stopReadAloudPlaybackEngines])
  );

  const clearOtherReadAloudTimers = useCallback(
    (messageId: string) => {
      Object.keys(readAloudLoadingTimeoutsRef.current).forEach((id) => {
        if (id !== messageId) {
          clearReadAloudLoadingTimer(id);
        }
      });
      Object.keys(readAloudPlaybackIntervalsRef.current).forEach((id) => {
        if (id !== messageId) {
          clearReadAloudPlaybackInterval(id);
        }
      });
    },
    [clearReadAloudLoadingTimer, clearReadAloudPlaybackInterval]
  );

  const startReadAloudPlaybackInterval = useCallback(
    (messageId: string) => {
      clearReadAloudPlaybackInterval(messageId);

      readAloudPlaybackIntervalsRef.current[messageId] = setInterval(() => {
        const currentState = readAloudByMessageIdRef.current[messageId];
        if (!currentState || !currentState.isActive || currentState.status !== "playing") {
          clearReadAloudPlaybackInterval(messageId);
          return;
        }

        const isGoogleTtsPlayback = googlePlaybackMessageIdRef.current === messageId;
        if (isGoogleTtsPlayback) {
          return;
        }

        const isStreamAutoPlayback = Boolean(currentState.isStreamAuto);
        const timerIncrementSeconds = isStreamAutoPlayback ? 1 : Math.max(playbackSpeedRef.current, 0.1);
        const nextElapsedSeconds = currentState.elapsedSeconds + timerIncrementSeconds;
        const completionTargetSeconds = currentState.totalSeconds;
        const isCompleted = isStreamAutoPlayback
          ? false
          : Math.min(nextElapsedSeconds, completionTargetSeconds) >= completionTargetSeconds;
        const cappedElapsed = Math.min(nextElapsedSeconds, completionTargetSeconds);
        const nextTotalSeconds = isStreamAutoPlayback
          ? Math.max(currentState.totalSeconds, nextElapsedSeconds)
          : currentState.totalSeconds;

        setReadAloudByMessageId((prev) => ({
          ...prev,
          [messageId]: {
            ...currentState,
            elapsedSeconds: cappedElapsed,
            totalSeconds: nextTotalSeconds,
            status: isCompleted ? "paused" : "playing",
            isActive: !isCompleted,
            hasPlayed: true,
          },
        }));

        if (isCompleted) {
          clearReadAloudPlaybackInterval(messageId);
        }
      }, READ_ALOUD_TICK_MS);
    },
    [clearReadAloudPlaybackInterval]
  );

  startReadAloudPlaybackIntervalRef.current = startReadAloudPlaybackInterval;

  const handleGoogleTtsFailure = useCallback(
    (messageId: string) => {
      stopMessageTtsSession();
      clearReadAloudTimersForMessage(messageId);
      googlePlaybackMessageIdRef.current = null;
      void messageReadAloudTtsRef.current.resetPlayback();
      setReadAloudByMessageId((prev) => {
        const st = prev[messageId] ?? createDefaultReadAloudState();
        return {
          ...prev,
          [messageId]: {
            ...st,
            status: "error",
            isActive: false,
          },
        };
      });
    },
    [clearReadAloudTimersForMessage, stopMessageTtsSession]
  );

  const finishGoogleReadAloudSession = useCallback(
    (messageId: string) => {
      if (googlePlaybackMessageIdRef.current !== messageId) {
        return;
      }
      stopMessageTtsSession();
      googlePlaybackMessageIdRef.current = null;
      setReadAloudByMessageId((prev) => {
        const st = prev[messageId] ?? createDefaultReadAloudState();
        const nextTotal = Math.max(st.totalSeconds, READ_ALOUD_MIN_SECONDS);
        return {
          ...prev,
          [messageId]: {
            ...st,
            status: "paused",
            isActive: false,
            hasPlayed: true,
            totalSeconds: nextTotal,
            elapsedSeconds: nextTotal,
          },
        };
      });
    },
    [stopMessageTtsSession]
  );

  const startGoogleReadAloud = useCallback(
    async (messageId: string, rawContent: string) => {
      const tts = messageReadAloudTtsRef.current;
      if (!tts) {
        return;
      }
      const plain = stripMarkdownForTts(rawContent);
      if (!plain) {
        handleGoogleTtsFailure(messageId);
        return;
      }

      clearReadAloudTimersForMessage(messageId);
      clearOtherReadAloudTimers(messageId);
      stopMessageTtsSession();

      const totalSeconds = estimateReadAloudDurationSeconds(rawContent);
      googlePlaybackMessageIdRef.current = messageId;

      setReadAloudByMessageId((prev) => {
        const next: Record<string, ReadAloudState> = {};
        Object.entries(prev).forEach(([id, state]) => {
          if (id === messageId) {
            next[id] = {
              ...state,
              status: "loading",
              isActive: true,
              elapsedSeconds: 0,
              totalSeconds,
              isStreamAuto: false,
            };
          } else if (state.isActive) {
            next[id] = { ...state, status: "paused", isActive: false };
          } else {
            next[id] = state;
          }
        });
        if (!next[messageId]) {
          next[messageId] = {
            ...createDefaultReadAloudState(),
            status: "loading",
            isActive: true,
            elapsedSeconds: 0,
            totalSeconds,
            isStreamAuto: false,
          };
        }
        return next;
      });

      await tts.resetPlayback();

      setMessageTtsHandlers({
        onPlaybackStart: () => {
          setReadAloudByMessageId((prev) => ({
            ...prev,
            [messageId]: {
              ...(prev[messageId] ?? createDefaultReadAloudState()),
              status: "playing",
              isActive: true,
              hasPlayed: true,
            },
          }));
        },
        onMetadata: (durationSeconds: number) => {
          setReadAloudByMessageId((prev) => {
            const st = prev[messageId] ?? createDefaultReadAloudState();
            return {
              ...prev,
              [messageId]: {
                ...st,
                totalSeconds: Math.max(st.totalSeconds, Math.ceil(durationSeconds)),
              },
            };
          });
        },
        onTimeUpdate: (currentTimeSeconds, durationSeconds) => {
          if (googlePlaybackMessageIdRef.current !== messageId) {
            return;
          }
          setReadAloudByMessageId((prev) => {
            const st = prev[messageId] ?? createDefaultReadAloudState();
            const nextTotal =
              durationSeconds > 0 ? Math.max(st.totalSeconds, Math.ceil(durationSeconds)) : st.totalSeconds;
            const cappedElapsed =
              nextTotal > 0 ? Math.min(Math.max(0, currentTimeSeconds), nextTotal) : Math.max(0, currentTimeSeconds);
            return {
              ...prev,
              [messageId]: {
                ...st,
                elapsedSeconds: cappedElapsed,
                totalSeconds: nextTotal,
              },
            };
          });
        },
        onPlaybackEnd: () => {
          finishGoogleReadAloudSession(messageId);
        },
        onError: () => {
          handleGoogleTtsFailure(messageId);
        },
      });

      try {
        await startMessageTtsSession(plain, messageId);
      } catch {
        handleGoogleTtsFailure(messageId);
      }
    },
    [
      clearOtherReadAloudTimers,
      clearReadAloudTimersForMessage,
      finishGoogleReadAloudSession,
      handleGoogleTtsFailure,
      setMessageTtsHandlers,
      startMessageTtsSession,
      stopMessageTtsSession,
    ]
  );

  const handlePauseReading = useCallback(
    (messageId: string) => {
      const currentState = readAloudByMessageIdRef.current[messageId] ?? createDefaultReadAloudState();
      if (!currentState.isActive || currentState.status !== "playing") {
        return;
      }

      if (googlePlaybackMessageIdRef.current === messageId) {
        pauseMessageTtsSession();
      } else {
        messageReadAloudTtsRef.current.suspendPlayback();
      }

      clearReadAloudPlaybackInterval(messageId);
      setReadAloudByMessageId((prev) => ({
        ...prev,
        [messageId]: { ...currentState, status: "paused" },
      }));
    },
    [clearReadAloudPlaybackInterval, pauseMessageTtsSession]
  );

  const handleStopReading = useCallback(
    (messageId: string) => {
      const currentState = readAloudByMessageIdRef.current[messageId] ?? createDefaultReadAloudState();
      if (!currentState.isActive && currentState.status !== "loading") {
        return;
      }

      if (currentState.isStreamAuto) {
        messageReadAloudTtsRef.current.onAutoStreamReadAloudStop?.(messageId);
      }
      stopMessageTtsSession();
      void messageReadAloudTtsRef.current.resetPlayback();
      googlePlaybackMessageIdRef.current = null;
      clearReadAloudTimersForMessage(messageId);
      setReadAloudByMessageId((prev) => ({
        ...prev,
        [messageId]: {
          ...currentState,
          status: "paused",
          isActive: false,
          elapsedSeconds: 0,
          hasPlayed: currentState.hasPlayed || currentState.status !== "loading",
        },
      }));
    },
    [clearReadAloudTimersForMessage, stopMessageTtsSession]
  );

  const handleResumeReading = useCallback(
    (messageId: string) => {
      const currentState = readAloudByMessageIdRef.current[messageId] ?? createDefaultReadAloudState();
      if (!currentState.isActive || currentState.status !== "paused") {
        return;
      }

      if (googlePlaybackMessageIdRef.current === messageId) {
        resumeMessageTtsSession();
      } else {
        messageReadAloudTtsRef.current.resumePlayback();
      }

      setReadAloudByMessageId((prev) => ({
        ...prev,
        [messageId]: { ...currentState, status: "playing" },
      }));
      if (googlePlaybackMessageIdRef.current !== messageId) {
        startReadAloudPlaybackInterval(messageId);
      }
    },
    [resumeMessageTtsSession, startReadAloudPlaybackInterval]
  );

  const handleReadAloudPress = useCallback(
    (messageId: string, content: string) => {
      const currentState = readAloudByMessageIdRef.current[messageId] ?? createDefaultReadAloudState();

      if (currentState.status === "loading" && currentState.isActive) {
        return;
      }

      if (currentState.isActive) {
        handleStopReading(messageId);
        return;
      }

      void startGoogleReadAloud(messageId, content);
    },
    [handleStopReading, startGoogleReadAloud]
  );

  const getAssistantReadAloudControl = useCallback(
    (messageId: string, content: string, meters: StreamReadAloudMeters): ReadAloudControl => {
      const readAloudState = readAloudByMessageId[messageId] ?? createDefaultReadAloudState();
      return {
        status: readAloudState.status,
        isActive: readAloudState.isActive,
        hasPlayed: readAloudState.hasPlayed,
        elapsedSeconds: readAloudState.elapsedSeconds,
        totalSeconds: readAloudState.totalSeconds,
        audioLevel: readAloudState.isStreamAuto ? meters.streamAudioLevel : undefined,
        audioLevels: readAloudState.isStreamAuto ? meters.streamAudioLevels : undefined,
        showTimer: !readAloudState.isStreamAuto,
        onPlayPress: () => handleReadAloudPress(messageId, content),
        onPause: () => handlePauseReading(messageId),
        onResume: () => handleResumeReading(messageId),
        onStop: () => handleStopReading(messageId),
      };
    },
    [handlePauseReading, handleReadAloudPress, handleResumeReading, handleStopReading, readAloudByMessageId]
  );

  return { getAssistantReadAloudControl };
}
